import { ref, computed } from "vue";
import { useTenderApi } from "@/services/api/useTenderApi";
import { useCustomerApi } from "@/services/api/useCustomerApi";
import type { Organization } from "@/stores/tender/types";
import { createCookieCache } from "@/utilities/cookieUtils";

export interface InvoiceRecipientParams {
  supplierId: number;
  priceRequestId: number;
  customerPropertyId: number;
  orgNumber: string;
}

export interface TenderInvoiceData {
  invoiceRecipientName?: string;
  invoiceRecipientEmail?: string;
  invoiceRecipientOrgNumber?: string;
  externalTenderId?: string;
}

export interface InvoiceRecipientResult {
  email: string;
  step: 1 | 2 | 3 | 4;
  externalReference?: string;
  receivingOrganization?: Organization;
}

export interface InvoiceRecipientError {
  type: "missing" | "offline" | "unknown";
  message: string;
}

export function useInvoiceRecipient() {
  // ===== STATE MANAGEMENT =====
  const recipient = ref<InvoiceRecipientResult | null>(null);
  const isLoading = ref(false);
  const error = ref<InvoiceRecipientError | null>(null);

  // ===== API INTEGRATION =====
  const { getRequestInvoiceRecipient } = useTenderApi();
  const { getPropertyInvoiceRecipient, getOrganizationInvoiceRecipient } =
    useCustomerApi();

  // ===== CACHE CONFIGURATION =====
  const CACHE_DURATION = 5 / 1440; // 5 minutes in days
  const invoiceRecipientCache = createCookieCache(
    "invoiceRecipient",
    CACHE_DURATION,
  );

  // ===== REQUEST CONTROL =====
  let abortController: AbortController | null = null;

  // ===== UTILITY FUNCTIONS =====

  /**
   * Generates a unique cache key for the given parameters
   * @param params - The invoice recipient parameters
   * @param tenderData - Optional tender data for cache key generation
   * @returns A string cache key
   */
  const getCacheKey = (
    params: InvoiceRecipientParams,
    tenderData?: TenderInvoiceData,
  ) =>
    `${params.supplierId}-${params.priceRequestId}-${tenderData?.externalTenderId || "no-tender-id"}`;

  /**
   * Checks if the API response is empty or invalid
   * @param data - The API response data to check
   * @returns True if the response is considered empty
   */
  const isResponseEmpty = (data: unknown): boolean => {
    return (
      data == null ||
      (Array.isArray(data) && data.length === 0) ||
      (typeof data === "object" && data !== null && !("email" in data))
    );
  };

  /**
   * Checks if the browser is currently offline
   * @returns True if the browser is offline
   */
  const checkOffline = (): boolean => {
    return typeof navigator !== "undefined" && navigator.onLine === false;
  };

  // ===== API FETCH FUNCTIONS =====

  /**
   * Step 1: Fetches invoice recipient at the request level (highest priority)
   * @param params - The invoice recipient parameters
   * @returns Promise resolving to InvoiceRecipientResult or null if not found
   */
  const fetchRequestRecipient = async (
    params: InvoiceRecipientParams,
  ): Promise<InvoiceRecipientResult | null> => {
    try {
      const result = await getRequestInvoiceRecipient({
        supplierId: params.supplierId,
        priceRequestId: params.priceRequestId,
      });

      if (!isResponseEmpty(result?.data) && result?.data?.email) {
        return {
          email: result.data.email,
          step: 1 as const,
          externalReference: result.data.externalReference,
          receivingOrganization: result.data.receivingOrganization,
        };
      }
      return null;
    } catch (error) {
      console.warn("Request recipient fetch failed:", error);
      return null;
    }
  };

  /**
   * Step 2: Fetches invoice recipient at the property level (medium priority)
   * @param params - The invoice recipient parameters
   * @returns Promise resolving to InvoiceRecipientResult or null if not found
   */
  const fetchPropertyRecipient = async (
    params: InvoiceRecipientParams,
  ): Promise<InvoiceRecipientResult | null> => {
    try {
      const result = await getPropertyInvoiceRecipient({
        orgNumber: params.orgNumber,
        customerPropertyId: params.customerPropertyId,
      });

      if (!isResponseEmpty(result?.data) && result?.data?.email) {
        return {
          email: result.data.email,
          step: 2 as const,
          externalReference: result.data.externalReference,
          receivingOrganization: result.data.receivingOrganization,
        };
      }
      return null;
    } catch (error) {
      console.warn("Property recipient fetch failed:", error);
      return null;
    }
  };

  /**
   * Step 3: Fetches invoice recipient at the organization level (lowest priority)
   * @param params - The invoice recipient parameters
   * @returns Promise resolving to InvoiceRecipientResult or null if not found
   */
  const fetchOrganizationRecipient = async (
    params: InvoiceRecipientParams,
  ): Promise<InvoiceRecipientResult | null> => {
    try {
      const result = await getOrganizationInvoiceRecipient({
        orgNumber: params.orgNumber,
      });

      if (!isResponseEmpty(result?.data) && result?.data?.email) {
        return {
          email: result.data.email,
          step: 3 as const,
          externalReference: result.data.externalReference,
          receivingOrganization: result.data.receivingOrganization,
        };
      }
      return null;
    } catch (error) {
      console.warn("Organization recipient fetch failed:", error);
      return null;
    }
  };

  /**
   * Step 4: Uses tender-level invoice recipient data as fallback (lowest priority)
   * @param tenderData - The tender invoice recipient data
   * @param bookingNumber - The booking number for fallback reference
   * @returns Promise resolving to InvoiceRecipientResult or null if not found
   */
  const fetchTenderRecipient = async (
    tenderData: TenderInvoiceData,
    bookingNumber: string | number,
  ): Promise<InvoiceRecipientResult | null> => {
    try {
      console.log("🔍 Step 4: Checking tender recipient data:", {
        tenderData,
        bookingNumber,
      });

      // Check if we have any tender invoice recipient data
      if (
        !tenderData.invoiceRecipientEmail &&
        !tenderData.invoiceRecipientName &&
        !tenderData.invoiceRecipientOrgNumber
      ) {
        console.log("❌ Step 4: No tender recipient data found");
        return null;
      }

      // Use tender data to create invoice recipient
      const result = {
        email: tenderData.invoiceRecipientEmail || "-",
        step: 4 as const,
        externalReference:
          tenderData.externalTenderId || bookingNumber.toString(),
        receivingOrganization: {
          id: 0, // Default ID for tender-level fallback
          name: tenderData.invoiceRecipientName || "-",
          orgNumber: tenderData.invoiceRecipientOrgNumber || "-",
          locked: false,
          publicActor: false,
          customerMargin: 0,
          bypassReallyInvoices: false,
          activeSubscription: "",
        },
      };

      console.log("✅ Step 4: Using tender recipient data:", result);
      return result;
    } catch (error) {
      console.warn("Tender recipient fetch failed:", error);
      return null;
    }
  };

  /**
   * Fetches invoice recipient with cascading fallback logic:
   * 1. Request-level recipient (highest priority)
   * 2. Property-level recipient (medium priority)
   * 3. Organization-level recipient (low priority)
   * 4. Tender-level recipient (lowest priority fallback)
   *
   * Each step is only called if the previous step fails to find a recipient.
   */
  const fetchInvoiceRecipient = async (
    params: InvoiceRecipientParams,
    tenderData?: TenderInvoiceData,
    bookingNumber?: string | number,
    setCurrentStep?: (step: 1 | 2 | 3 | 4 | null) => void,
  ) => {
    // Cancel any existing request
    if (abortController) {
      abortController.abort();
    }

    // Reset state
    error.value = null;
    recipient.value = null;

    // Check cache first
    const cacheKey = getCacheKey(params, tenderData);
    const cached =
      invoiceRecipientCache.getFromCache<InvoiceRecipientResult>(cacheKey);
    if (cached) {
      recipient.value = cached;
      return;
    }

    // Check if offline
    if (checkOffline()) {
      error.value = {
        type: "offline",
        message: "Kan ikke nå serveren.",
      };
      return;
    }

    isLoading.value = true;
    abortController = new AbortController();

    try {
      // Step 1: Try request-level recipient first (highest priority)
      const requestResult = await fetchRequestRecipient(params);
      if (requestResult) {
        setCurrentStep?.(1);
        recipient.value = requestResult;
        invoiceRecipientCache.saveToCache(cacheKey, requestResult);
        return;
      }

      // Step 2: Try property-level recipient (medium priority)
      const propertyResult = await fetchPropertyRecipient(params);
      if (propertyResult) {
        setCurrentStep?.(2);
        recipient.value = propertyResult;
        invoiceRecipientCache.saveToCache(cacheKey, propertyResult);
        return;
      }

      // Step 3: Try organization-level recipient (lowest priority)
      const organizationResult = await fetchOrganizationRecipient(params);
      if (organizationResult) {
        setCurrentStep?.(3);
        recipient.value = organizationResult;
        invoiceRecipientCache.saveToCache(cacheKey, organizationResult);
        return;
      }

      // Step 4: Try tender-level recipient as final fallback
      if (tenderData && bookingNumber) {
        console.log("🔄 Attempting Step 4 with data:", {
          tenderData,
          bookingNumber,
        });
        const tenderResult = await fetchTenderRecipient(
          tenderData,
          bookingNumber,
        );
        if (tenderResult) {
          console.log("✅ Step 4 SUCCESS: Setting current step to 4");
          setCurrentStep?.(4);
          recipient.value = tenderResult;
          invoiceRecipientCache.saveToCache(cacheKey, tenderResult);
          return;
        }
        console.log("❌ Step 4: No result returned");
      } else {
        console.log("❌ Step 4: Missing data", {
          hasTenderData: !!tenderData,
          hasBookingNumber: !!bookingNumber,
          tenderData,
          bookingNumber,
        });
      }

      // No recipient found at any level
      setCurrentStep?.(null);
      error.value = {
        type: "missing",
        message:
          "Mangler fakturamottaker — vennligst opprett en eller kontakt support.",
      };
    } catch (err) {
      if (err instanceof Error && err.name === "AbortError") {
        // Request was cancelled, don't set error
        return;
      }

      console.error("Failed to fetch invoice recipient:", err);
      error.value = {
        type: "unknown",
        message: "Kunne ikke laste informasjon om fakturamottaker.",
      };
    } finally {
      isLoading.value = false;
      abortController = null;
    }
  };

  // ===== CONTROL FUNCTIONS =====

  /**
   * Cancels any ongoing invoice recipient fetch request
   */
  const cancel = () => {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    isLoading.value = false;
  };

  /**
   * Clears cached invoice recipient data
   * @param params - Optional specific parameters to clear. If not provided, warns about clearing all cache
   * @param tenderData - Optional tender data for cache key generation
   */
  const clearCache = (
    params?: InvoiceRecipientParams,
    tenderData?: TenderInvoiceData,
  ) => {
    if (params) {
      // Clear specific cache entry
      const cacheKey = getCacheKey(params, tenderData);
      invoiceRecipientCache.removeFromCache(cacheKey);
    } else {
      // Note: Cookie cache doesn't have a clear all method by design
      // This is intentional to prevent accidental data loss
      console.warn(
        "To clear all invoice recipient cache, specific parameters are required",
      );
    }
  };

  return {
    recipient: computed(() => recipient.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    fetchInvoiceRecipient,
    cancel,
    clearCache,
  };
}
